import {LocalStyleSSR} from './local-style';
import {CSSStringWriter} from '@packages/systems/style/writers';
import {Breakpoint} from '@packages/systems/style/media-queries';
import {
  DefaultStyle,
  atomConfigToDefaultStyleConfig,
  jsonNodeSymbolRootToDefaultStyleConfig,
} from './default-style';
import {
  Component,
  Expressions,
  matchExpression,
  Plugins,
  parseType,
  Type,
  Types,
} from '@packages/systems/wfdl';
import {WebflowEnvironments} from '@packages/systems/core-constants';
import type {JSONNode} from '@packages/shared/types/JSONNode';
import {migrateJsonNodeFromTagToType} from '@packages/systems/core/utils/TagToTypeMigrationUtils';
import {type Expression} from '@packages/systems/wfdl-ast';
import {qualify} from '@packages/systems/wfdl-Qualified';
import {
  rootIds as rootIds_,
  indexJsonDomNodeArrayById,
  migrateFlattenOverrides,
  dataTypesFromSymbolNodes,
} from '@packages/systems/core/render';
import {
  forEachTree,
  isSymbolRoot,
  getSymbolRootIdFromInstance,
  getSlotDefaultSymbolIds,
} from '@packages/systems/core/utils/JsonDomUtils';
import {weakMemo} from '@packages/utilities/memo';

const rootIds = weakMemo((plugins: Plugins.PluginsType) =>
  weakMemo((dataTypes: Plugins.DataTypes) =>
    weakMemo(rootIds_(plugins, dataTypes))
  )
);

type StyleBlockId = string;
type AtomType = string;

type ForwardingState = {
  // innerProp -> rootId -> the outer props that root forwards into innerProp
  edges: Map<string, Map<string, string[]>>;
  indexedRootIds: Set<string>;
};

type ForwardingFailureStage = 'index' | 'resolve';

type AddPageOptions = {
  // addPage throws if this stylesheet has already added a page.
  // compileStylesPerPage builds a new SSRStylesheet for each page.
  resolveForwardedSymbolOverrides?: boolean;
};

/**
 * Given an unparsed JSON `dataType` string, attempt to parse it as a dataType
 * and extract its fields.
 *
 * Returns `undefined` if anything goes wrong attempting to extract the record
 * fields, otherwise returns a POJO map of field ID -> Type.
 */
function parseDataTypeFields(
  dataTypeInput: string | unknown
): {[prop: string]: Type} | undefined {
  let dataTypeJson;
  try {
    dataTypeJson =
      typeof dataTypeInput === 'string'
        ? JSON.parse(dataTypeInput)
        : dataTypeInput;
  } catch (error) {
    // Handle malformed JSON gracefully
    return;
  }

  const dataTypeResult = parseType(dataTypeJson);
  if (dataTypeResult.tag === 'Error') {
    return;
  }

  const dataType = dataTypeResult.val;

  const fields = Types.getRecordFields(dataType);
  if (fields == null) {
    return;
  }

  return fields;
}

function getDefaultClassesFromFields(
  fields: {[prop: string]: Type} | undefined,
  selectProp: string
): string | undefined {
  const field = fields?.[selectProp];
  if (field == null) {
    return;
  }
  const defaultValue = Types.getDefaultValue(field, undefined);
  if (typeof defaultValue !== 'string') {
    return;
  }
  return defaultValue;
}

// Helper function to extract classes from dataType for a specific selectProp
function getDefaultClassesFromDataType(
  dataTypeInput: string | unknown,
  selectProp: string
): string | undefined {
  return getDefaultClassesFromFields(
    parseDataTypeFields(dataTypeInput),
    selectProp
  );
}

// Reads the dataTypes parsed once per publish; don't re-parse the root's
// dataType JSON on this per-page path.
function getSiteComponentFields(
  dataTypes: Plugins.DataTypes,
  symbolRootId: string
): {[prop: string]: Type} | undefined {
  const dataType = dataTypes.get(
    qualify(Plugins.name('__SitePlugin'), Component.name(symbolRootId))
  );
  return (dataType && Types.getRecordFields(dataType)) ?? undefined;
}

// compileStylesPerPage builds an SSRStylesheet per page over the same symbol
// node index, so keying on it shares one forwarding index across the publish.
const getForwardingState = weakMemo(
  (_symbolNodeIndex: {[nodeId: string]: JSONNode}): ForwardingState => ({
    edges: new Map(),
    indexedRootIds: new Set(),
  })
);

// Records the `bind.sym.overrides` entries that select from the parent
// component's data: the props a component forwards into a nested instance.
function addForwardingEdges(
  state: ForwardingState,
  symbolRootId: string,
  bind: Expression
): void {
  const sym = Expressions.getRecord(bind)?.sym;
  const overrides = sym && Expressions.getRecord(sym)?.overrides;
  const overrideFields = overrides && Expressions.getRecord(overrides);
  if (!overrideFields) return;

  for (const [innerProp, valueExpression] of Object.entries(overrideFields)) {
    const select = valueExpression && Expressions.getSelect(valueExpression);
    if (!select || !Expressions.isDataVariable(select.from)) continue;

    let byRoot = state.edges.get(innerProp);
    if (!byRoot) {
      byRoot = new Map();
      state.edges.set(innerProp, byRoot);
    }
    const outerProps = byRoot.get(symbolRootId);
    if (!outerProps) {
      byRoot.set(symbolRootId, [select.prop]);
    } else if (!outerProps.includes(select.prop)) {
      outerProps.push(select.prop);
    }
  }
}

// Adds the classes that reach the page's class props through forwarding. Each
// prop is expanded at most once per page, so the work follows the edges walked,
// not how many class props share an upstream prop.
function addForwardedClasses(
  classProps: ReadonlySet<string>,
  seenStringOverrides: ReadonlyMap<string, ReadonlySet<string>>,
  state: ForwardingState,
  walkedRootIds: ReadonlySet<string>,
  dataTypes: Plugins.DataTypes,
  addClasses: (classes: string) => void
): void {
  // Class props start expanded: the bind loop already added their own overrides.
  const expanded = new Set(classProps);
  const stack = [...classProps];
  while (stack.length > 0) {
    const innerProp = stack.pop();
    if (innerProp === undefined) continue;
    const byRoot = state.edges.get(innerProp);
    if (!byRoot) continue;
    // Scan whichever side is smaller; duplicated components can share hot prop IDs.
    const rootIds =
      byRoot.size <= walkedRootIds.size
        ? byRoot.keys()
        : walkedRootIds.values();
    for (const rootId of rootIds) {
      if (!walkedRootIds.has(rootId)) continue;
      const outerProps = byRoot.get(rootId);
      if (outerProps === undefined) continue;
      const fields = getSiteComponentFields(dataTypes, rootId);
      for (const outerProp of outerProps) {
        // An instance without an override renders the outer prop's default.
        const defaultValue = getDefaultClassesFromFields(fields, outerProp);
        if (defaultValue) addClasses(defaultValue);
        if (expanded.has(outerProp)) continue;
        expanded.add(outerProp);
        for (const value of seenStringOverrides.get(outerProp) ?? []) {
          addClasses(value);
        }
        stack.push(outerProp);
      }
    }
  }
}

export class SSRStylesheet {
  localStyle: LocalStyleSSR;
  defaultStyle: DefaultStyle<string>;
  foundStyles: Set<StyleBlockId>;
  // These are class names found for component class attribute overrides
  foundClasses: Set<string>;

  /**
   * ## Unfortunate Hack
   * Our integration with Stripe.js requires that we compile explicit
   * pseudo classes e.g. `-wfp-focus` along with a runtime to pierce
   * the iFrame boundary. In order to do this we keep track of which
   * style blocks are attached to which element types so that we can
   * compile these classes correctly when we encounter them.
   */
  styleBlockIdsToTypes: Map<StyleBlockId, Set<AtomType>>;
  plugins: Plugins.PluginsType;
  symbolRootsVisited: Set<string> = new Set();
  forwardedSymbolOverridesFailed = false;
  forwardedSymbolOverridesError: unknown = undefined;
  forwardedSymbolOverridesFailureStage: ForwardingFailureStage | undefined =
    undefined;
  // symbolRootsVisited, foundClasses, and the forwarding failure fields stick
  // to the instance. Once this stylesheet has resolved forwarded classes, another
  // page on it would inherit them whether or not that page asks for resolution.
  #addPageCount = 0;
  #resolvedForwardedSymbolOverrides = false;
  #dataTypes: Plugins.DataTypes;
  #symbolNodeIndex: {
    [nodeId: string]: JSONNode;
  };
  #symbolRoots: {
    [nodeId: string]: JSONNode;
  };

  constructor(
    symbolJsonNodes: JSONNode[],
    breakpoints: Array<Breakpoint>,
    plugins: Plugins.PluginsType
  ) {
    this.plugins = plugins;
    this.styleBlockIdsToTypes = new Map();
    this.localStyle = new LocalStyleSSR(breakpoints);
    this.defaultStyle = new DefaultStyle(
      new CSSStringWriter(),
      breakpoints,
      WebflowEnvironments.SSR
    );
    this.foundStyles = new Set<StyleBlockId>();
    this.foundClasses = new Set<string>();

    const dataTypes = dataTypesFromSymbolNodes(plugins)(symbolJsonNodes);
    const migratedSymbolNodes =
      hack_migrateJsonNodes(plugins)(dataTypes)(symbolJsonNodes);
    this.#dataTypes = dataTypes;
    this.#symbolNodeIndex = indexJsonDomNodeArrayById(migratedSymbolNodes);
    this.#symbolRoots = rootIds(plugins)(dataTypes)(migratedSymbolNodes).reduce<
      Record<string, JSONNode>
    >((roots, id) => {
      const rootNode = this.#symbolNodeIndex[id];
      if (rootNode && isSymbolRoot(rootNode)) {
        roots[id] = rootNode;
      }
      return roots;
    }, {});
  }

  getDefaultStylesheet() {
    return this.defaultStyle.commit();
  }

  getLocalStylesheet() {
    return this.localStyle.commit();
  }

  addToFoundClasses(classes: string | undefined) {
    if (classes) {
      const customClasses = classes.trim().split(/\s+/);
      for (const cls of customClasses) {
        if (cls) {
          this.foundClasses.add(cls);
        }
      }
    }
  }

  // Unified method to extract and process attributes from any bind structure
  processBindAttributes(
    bind: unknown,
    seenStringOverrides: Map<string, Set<string>>,
    childNode: JSONNode,
    symbolRootDataType?: string | unknown,
    classProps?: Set<string>
  ): void {
    const extractAttributesFromBind = matchExpression({
      Record: (fields) => {
        // Try xattr first (for Expression objects)
        if (fields.xattr) {
          return matchExpression({
            List: (items) => items,
            _: () => null,
          })(fields.xattr);
        }

        // Try attributes second (for plain objects or alternative Expression structure)
        if (fields.attributes) {
          return matchExpression({
            List: (items) => items,
            _: () => null,
          })(fields.attributes);
        }

        return null;
      },
      _: () => null,
    });

    const attributesList = extractAttributesFromBind(bind as Expression);
    if (attributesList && Array.isArray(attributesList)) {
      // Process each attribute using matchExpression with fallbacks for plain objects
      for (const attr of attributesList) {
        const processAttribute = matchExpression({
          Record: (attrFields) => {
            const nameField = attrFields.name;
            const valueField = attrFields.value;

            if (!nameField || !valueField) return;

            // Extract name - handle both Expression and plain object
            const attrName = matchExpression({
              Text: (text) => text,
              _: () => {
                // Fallback for plain objects
                return (nameField as {val?: string})?.val;
              },
            })(nameField);

            if (attrName !== 'class') return;

            // Extract value - handle both Expression and plain object
            const classSet = matchExpression({
              Text: (text) => new Set([text]),
              Select: (select) => {
                if (classProps && Expressions.isDataVariable(select.from)) {
                  classProps.add(select.prop);
                }
                const overrideClasses = seenStringOverrides.get(select.prop);
                const resultSet = new Set<string>(overrideClasses ?? []);

                // Always consult the dataType default. Instances without an
                // override render with the default value, so the default class
                // name must also be emitted alongside any override values.
                // Prefer dataType on the bind's host node; fall back to the
                // enclosing symbol root's dataType when the bind lives on a
                // descendant element (the root holds the dataType).
                const dataTypeForLookup =
                  childNode.dataType ?? symbolRootDataType;
                if (dataTypeForLookup) {
                  const defaultClasses = getDefaultClassesFromDataType(
                    dataTypeForLookup,
                    select.prop
                  );
                  if (defaultClasses) {
                    resultSet.add(defaultClasses);
                  }
                }

                return resultSet.size > 0 ? resultSet : null;
              },
              _: () => null,
            })(valueField);

            if (classSet == null) return;

            for (const classesString of classSet) {
              this.addToFoundClasses(classesString);
            }
          },
          _: () => {},
        });

        processAttribute(attr);
      }
    }
  }

  addPage(
    pageJsonNodes: JSONNode[],
    pageId: string = '',
    {resolveForwardedSymbolOverrides = false}: AddPageOptions = {}
  ) {
    if (
      (resolveForwardedSymbolOverrides ||
        this.#resolvedForwardedSymbolOverrides) &&
      this.#addPageCount > 0
    ) {
      throw new Error(
        'resolveForwardedSymbolOverrides requires one SSRStylesheet per page'
      );
    }
    this.#addPageCount += 1;
    this.#resolvedForwardedSymbolOverrides =
      this.#resolvedForwardedSymbolOverrides || resolveForwardedSymbolOverrides;

    const migratedPageNodes = hack_migrateJsonNodes(this.plugins)(
      this.#dataTypes
    )(pageJsonNodes);

    const bodyNode =
      migratedPageNodes.find((node) => node.type === 'Body') || null;

    const visit: Array<{
      rootNode: JSONNode;
      nodeIndex: {
        [key: string]: JSONNode;
      };
    }> = [];

    if (bodyNode) {
      const pageNodeIndex = indexJsonDomNodeArrayById(migratedPageNodes);
      visit.push({rootNode: bodyNode, nodeIndex: pageNodeIndex});
    }

    /**
     * Keeps track of all seen string overrides per field ID.
     *
     * We later use these to determine all seen class names if the field is referenced
     * in a `class` attribute.
     */
    const seenStringOverrides = new Map<string, Set<string>>();

    // Binds resolve after the walk: the instances supplying a field's overrides
    // can come later, and symbolRootsVisited means the root is never revisited.
    const pendingBinds: Array<{
      bind: unknown;
      childNode: JSONNode;
      symbolRootDataType: unknown;
    }> = [];
    const forwardingState = resolveForwardedSymbolOverrides
      ? getForwardingState(this.#symbolNodeIndex)
      : undefined;
    const recordForwardingError = (
      stage: ForwardingFailureStage,
      error: unknown
    ) => {
      if (this.forwardedSymbolOverridesFailed) return;
      this.forwardedSymbolOverridesFailed = true;
      this.forwardedSymbolOverridesError = error;
      this.forwardedSymbolOverridesFailureStage = stage;
    };

    while (visit.length > 0) {
      // @ts-expect-error - TS2339 - Property 'rootNode' does not exist on type '{ rootNode: JSONNode; nodeIndex: { [key: string]: JSONNode; }; } | undefined'. | TS2339 - Property 'nodeIndex' does not exist on type '{ rootNode: JSONNode; nodeIndex: { [key: string]: JSONNode; }; } | undefined'.
      const {rootNode, nodeIndex} = visit.pop();
      const rootNodeId = rootNode._id;

      if (this.symbolRootsVisited.has(rootNodeId)) continue;

      let pageOrSymbolIdForLocalStyle = pageId;
      if (isSymbolRoot(rootNode)) {
        this.symbolRootsVisited.add(rootNodeId);
        pageOrSymbolIdForLocalStyle = rootNodeId;
        this.addSymbol(rootNode);

        // Components referenced only by this definition's slot defaults have no
        // instance node anywhere, so the tree walk below never reaches them and
        // their styles would be missing from the page.
        for (const defaultSymbolId of getSlotDefaultSymbolIds(rootNode)) {
          if (this.symbolRootsVisited.has(defaultSymbolId)) continue;

          const defaultSymbolRootNode = this.#symbolRoots[defaultSymbolId];
          if (defaultSymbolRootNode) {
            visit.push({
              rootNode: defaultSymbolRootNode,
              nodeIndex: this.#symbolNodeIndex,
            });
          }
        }
      }

      // When traversing inside a symbol root, the dataType lives on the root
      // node. Capture it here so binds on descendant elements can fall back
      // to it for default-value lookup.
      const currentSymbolRootDataType = isSymbolRoot(rootNode)
        ? rootNode.dataType
        : undefined;
      const shouldIndexRoot =
        forwardingState !== undefined &&
        isSymbolRoot(rootNode) &&
        !forwardingState.indexedRootIds.has(rootNodeId);

      forEachTree(rootNode, nodeIndex, (childNode) => {
        if (childNode.classes) {
          childNode.classes.forEach((styleBlockId) => {
            this.foundStyles.add(styleBlockId);
          });
        }

        if (childNode.type === 'Symbol') {
          // gather overrides for handling custom connected class attribute - which are strings
          const overridesObj: Record<string, unknown> =
            childNode.data?.sym.overrides;
          if (overridesObj) {
            Object.entries(overridesObj).forEach(
              ([overrideKey, overrideValue]) => {
                // Store override values as-is, only process as classes when needed
                if (typeof overrideValue !== 'string') {
                  return;
                }

                const stringsForThisKey =
                  seenStringOverrides.get(overrideKey) ?? new Set<string>();
                stringsForThisKey.add(overrideValue);
                seenStringOverrides.set(overrideKey, stringsForThisKey);
              }
            );
          }

          if (
            forwardingState &&
            shouldIndexRoot &&
            typeof childNode.bind === 'object' &&
            childNode.bind !== null
          ) {
            try {
              addForwardingEdges(forwardingState, rootNodeId, childNode.bind);
            } catch (error) {
              recordForwardingError('index', error);
            }
          }
        }

        // handle static class attributes (if there are only static attributes)
        if (Array.isArray(childNode.data?.xattr)) {
          childNode.data.xattr.forEach((obj) => {
            if (obj.name === 'class') {
              // This is a space separated string of class names
              this.addToFoundClasses(obj.value);
            }
          });
        }

        // handle bound class attributes (if ANY attribute is bound then even static ones show up here)
        if (childNode.bind) {
          pendingBinds.push({
            bind: childNode.bind,
            childNode,
            symbolRootDataType: currentSymbolRootDataType,
          });
        }

        this.localStyle.addNode(childNode, pageOrSymbolIdForLocalStyle);
        this.addAtom(childNode);

        const symbolRootId = getSymbolRootIdFromInstance(childNode);
        if (symbolRootId && !this.symbolRootsVisited.has(symbolRootId)) {
          const symbolRootNode = this.#symbolRoots[symbolRootId];

          if (symbolRootNode) {
            visit.push({
              rootNode: symbolRootNode,
              nodeIndex: this.#symbolNodeIndex,
            });
          }
        }
      });

      if (forwardingState && shouldIndexRoot) {
        forwardingState.indexedRootIds.add(rootNodeId);
      }
    }

    const classProps = forwardingState ? new Set<string>() : undefined;
    for (const {bind, childNode, symbolRootDataType} of pendingBinds) {
      // Use the unified method to process attributes from both Expression objects and plain objects
      this.processBindAttributes(
        bind,
        seenStringOverrides,
        childNode,
        symbolRootDataType,
        classProps
      );
    }

    if (forwardingState && classProps) {
      const forwardedClasses: string[] = [];
      try {
        addForwardedClasses(
          classProps,
          seenStringOverrides,
          forwardingState,
          this.symbolRootsVisited,
          this.#dataTypes,
          (classes) => forwardedClasses.push(classes)
        );
      } catch (error) {
        recordForwardingError('resolve', error);
        // A page whose forwarded pass fails keeps exactly the flag-off output.
        forwardedClasses.length = 0;
      }
      for (const classes of forwardedClasses) {
        this.addToFoundClasses(classes);
      }
    }
  }

  addAtom(node: JSONNode) {
    const type = node.type;

    if (type == null || node.text) return;

    this.defaultStyle.addDefaultStyle(
      type,
      atomConfigToDefaultStyleConfig(Plugins.getAtomByType(this.plugins, type))
    );

    const styleBlockIds = node.classes;

    if (!Array.isArray(styleBlockIds) || styleBlockIds.length === 0) return;

    for (const styleBlockId of styleBlockIds) {
      const atomTypeSet = this.styleBlockIdsToTypes.get(styleBlockId);

      if (atomTypeSet == null) {
        this.styleBlockIdsToTypes.set(styleBlockId, new Set([type]));
      } else {
        atomTypeSet.add(type);
      }
    }
  }

  addSymbol(node: JSONNode) {
    return this.defaultStyle.addDefaultStyle(
      node._id,
      jsonNodeSymbolRootToDefaultStyleConfig(node)
    );
  }

  commit() {
    return {
      localStylesheet: this.localStyle.commit(),
      defaultStylesheet: this.defaultStyle.commit(),
      foundStyles: this.foundStyles,
      foundClasses: this.foundClasses,
    };
  }
}

/**
 * HACK: These migrations are hardcoded into JSONNode to Expression reification
 *
 * Because SSRStylesheet operates on JSON nodes, we need to run these hardcoded
 * migrations manually for the JSON nodes that SSRStylesheet receives.
 *
 * This hack can be removed once these migrations are performed via the normal
 * JSONNode migration process.
 */
const hack_migrateJsonNodes = weakMemo((plugins: Plugins.PluginsType) =>
  weakMemo((dataTypes: Plugins.DataTypes) =>
    weakMemo(function hack_migrateJsonNodes_inner(nodes: JSONNode[]) {
      return migrateFlattenOverrides(plugins, dataTypes, nodes).map(
        migrateJsonNodeFromTagToType
      );
    })
  )
);
